import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { readOnly, alias, sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { showModal } from "sh-www/utils/modal-controls";
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { SH_INVOICE } from 'sh-www/models/sh-invoice';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { absoluteStoryhunterUrl } from 'sh-www/utils/url';
import { makeMoney } from 'sh-www/helpers/make-money';
import { BILLABLE_RECORD } from 'sh-www/models/billable-record';
import { task } from 'ember-concurrency';

const CLASS_NAME = 'SH-c-sh-invoice-view';

class AdminAccountingPayinInvoiceController extends Controller {
  /**
   * Register services
   */
  @service('commands/application-commands') applicationCommandService;
  @service('state-managers/application') applicationStateManager;
  @service constants;
  @service cookies;
  @service moment;
  @service('commands/sh-invoice-commands') shInvoiceCommandService;
  @service('states/routes/admin/accounting/payin/sh-invoice') state;
  @service('state-managers/routes/admin/accounting/payin/sh-invoice') stateManager;

  /**
   * Will be set to true when we're saving the triggeredAt date.
   *
   * @type {Boolean}
   */
  _isSavingNewInvoiceDate = false;

  /** @type {Object} */
  newLineItem = {
    description: '',
    amount: '',
    type: 'adjustment'
  };

  /** @type {Array<String>} */
  _shInvoiceActionsSort = ['createdAt:asc'];

  /** @type {Boolean} */
  unbatchLoading = false;

  @sort('state.shInvoice.actions', '_shInvoiceActionsSort')
  _shInvoiceActions;

  /**
   * Get the status text of the invoice
   *
   * @type {String}
   */
  @readOnly('state.shInvoice.statusText')
  _status;

  /** @type {Boolean} */
  @readOnly('state.shInvoice.isFinal')
  _canSend;

  @readOnly('state.isSendingInvoice')
  sendLoading;

  get className() {
    return CLASS_NAME;
  }

  @computed('state.shInvoice.isBatchedInvoice','state.shInvoice.applicationId','state.shInvoice.childShInvoices.@each.applicationId')
  get _applicationLinks() {
    if (!this.state.shInvoice.isBatchedInvoice) {
      const applicationId = this.state.shInvoice.applicationId;
      return htmlSafe(`<a href="/project/${applicationId}" target="_blank">${applicationId}</a>`);
    }

    let content = '';
    this.state.shInvoice.childShInvoices.forEach((childShInvoice) => {
      const applicationId = childShInvoice.applicationId;
      content += `<a href="/project/${applicationId}" target="_blank">${applicationId}</a>`;
    });

    return htmlSafe(content);
  }

  /** @type {Number} */
  @computed('state.shInvoice.amount','state.shInvoice.shInvoiceLineItems.@each.amount','state.shInvoice.isBatchedInvoice')
  get _invoiceTotalAmount() {
    if (this.state.shInvoice.isBatchedInvoice) {
      return this.state.shInvoice.amount;
    }

    return this.state.shInvoice.shInvoiceLineItems.reduce((carry, shInvoiceLineItem) => {
      return carry + shInvoiceLineItem.amount;
    }, 0);
  }

  /** @type {Boolean} */
  @computed('state.shInvoice.isBatchedInvoice')
  get _canEditFees() {
    return !this.state.shInvoice.isBatchedInvoice;
  }

  /**
   * @type {Boolean}    True if admin can mark all child invoices as paid.
   */
  @computed('state.shInvoice.isPaid')
  get _canMarkAllAsPaid() {
    return !this.state.shInvoice.isPaid;
  }

  /** @type {Boolean} */
  @computed('state.shInvoice.{isFinal,isPaid}')
  get _canProcess() {
    return this.state.shInvoice.isFinal && !this.state.shInvoice.isPaid;
  }

  @computed('state.shInvoice.isBatchedInvoice')
  get _canReconcile() {
    return !this.state.shInvoice.isBatchedInvoice;
  }

  /**
   * @type {String}      A comma delimited string of invoice ids easy to copy and paste.
   */
  @computed('state.shInvoice.childShInvoices.length')
  get _invoiceSearchString() {
    if (!this.state.shInvoice.childShInvoices) {
      return '';
    }

    let invoiceIds = this.state.shInvoice.childShInvoices.map((shInvoice) => {
      return shInvoice.id;
    });

    return invoiceIds.join(',');
  }

  @task
  *_removeFromBatchTask(childShInvoice) {
    yield this.shInvoiceCommandService.removeFromBatch(childShInvoice);

    yield this.stateManager.loadShInvoice(this.state.shInvoice.id);
  }

  /**
   * Get a 'paid' pseudo status class of the ShInvoice
   *
   * @type {String}     class for the text
   */
  @computed('state.shInvoice.statusPaidText')
  get _statusPaidTextClass() {
    let statusClass = '';

    switch (this.state.shInvoice.statusPaidText) {
      case SH_INVOICE.PAID_STATUS_TEXT.PARTIALLY_PAID:
        return statusClass + ' lsg-yellow';
      case SH_INVOICE.PAID_STATUS_TEXT.PAID_EXACTLY:
        return statusClass + ' lsg-green';
      case SH_INVOICE.PAID_STATUS_TEXT.OVERPAID:
        return statusClass + ' lsg-green';
      case SH_INVOICE.PAID_STATUS_TEXT.UNPAID:
        return statusClass + ' lsg-yellow';
    }

    return statusClass + ' lsg-dark-gray';
  }

  /** @type {String} */
  @computed('state.shInvoice.lastSentAt')
  get _statusSentText() {
    const lastSentAt = this.state.shInvoice.lastSentAt;
    if (!lastSentAt) {
      return 'Not Sent';
    }

    const lastSentMoment = this.moment.moment(lastSentAt).format('YYYY.MM.DD');
    return `Last sent ${lastSentMoment}`;
  }

  /**
   * @type {String}     Get the css class for the sent status
   */
  @computed('state.shInvoice.lastSentAt')
  get _statusSentTextClass() {
    const lastSentAt = this.state.shInvoice.lastSentAt;
    let statusClass = '';

    if (!lastSentAt) {
      return `${statusClass} lsg-yellow`;
    }

    return `${statusClass} lsg-green`;
  }

  @computed('state.shInvoice.agreement')
  get _agreementText() {
    switch (this.state.shInvoice.agreement) {
      case BILLABLE_RECORD.AGREEMENT.SELLER_APPROVED:
        return 'Seller Agreed';
      case BILLABLE_RECORD.AGREEMENT.BUYER_APPROVED:
        return 'Buyer Agreed';
      case BILLABLE_RECORD.AGREEMENT.AGREED:
        return 'Both Agreed';
      default:
        return 'Unknown';
    }
  }

  @computed('state.shInvoice.agreement')
  get _agreementTextClass() {
    let statusClass = '';

    if (this.state.shInvoice.agreement === BILLABLE_RECORD.AGREEMENT.AGREED) {
      return statusClass + ' lsg-green';
    }

    return statusClass + ' lsg-yellow';
  }

  @computed('state.shInvoice.approved')
  get _approvedText() {
    if (this.state.shInvoice.approved) {
      return 'Approved';
    }

    return 'Un-approved';
  }

  @computed('state.shInvoice.approved')
  get _approvedTextClass() {
    const statusClass = '';

    if (!this.state.shInvoice.approved) {
      return statusClass + ' lsg-yellow';
    }

    return statusClass + ' lsg-green';
  }

  /**
   * The memo for the current invoice
   *
   * NOTE - ALIASED TO THE TOP LEVEL STATE.
   *
   * @type {String}
   */
  @alias('state.updatedMemoText')
  _updatedMemoText;

  @action
  addTransaction() {
    const modalModel = {
      shInvoice: this.state.shInvoice,
      shInvoiceTransaction: null,
      onTransactionEdited: () => {
        this.applicationStateManager.addToast('Transaction Added');
        this.stateManager.loadShInvoice(this.state.shInvoice.id);
      },
      markFullyPaid: false,
    };

    showModal(this, 'admin/edit-sh-invoice-transaction-modal', modalModel);
  }

  @action
  approve() {
    showModal(this, 'confirmation', {
      isShowCancelButton: true,
      titleText: 'Approve expedited invoice?',
      bodyText: `Are you sure you want to approve expedited payment for ${makeMoney([this.state.shInvoice.nonFeesAmount])}?`,
      confirmAction: () => {
        this.stateManager.approve();
      }
    });
  }

  @action
  declineExpedite() {
    showModal(this, 'confirmation', {
      isShowCancelButton: true,
      titleText: 'Decline expedited payment?',
      bodyText: `Are you sure? This will remove expedited payment for this amount ${makeMoney([this.state.shInvoice.nonFeesAmount])} and release payment on a non-expedited basis.`,
      confirmAction: () => {
        this.stateManager.declineExpedite();
      }
    });
  }

  /**
   * @param {ShInvoiceTransaction} shInvoiceTransaction
   */
  @action
  deleteTransaction(shInvoiceTransaction) {
    showModal(this, 'confirmation', {
      titleText: 'Reverse Transaction',
      bodyText: 'Are you sure you want to reverse this transaction? Credit Card payments will be automatically refunded and will NOT be re-processed until you Process or Reconcile again.',
      actionButtonText: 'Okay',
      isShowCancelButton: true,
      confirmAction: () => {
        this.stateManager.deleteTransaction(shInvoiceTransaction).then(() => {
          this.applicationStateManager.addToast('Transaction Deleted');
          this.stateManager.loadShInvoice(this.state.shInvoice.id);
        }).catch((e) => {
          handleErrorResponseWithModal(
            this,
            e,
            'There was an error reversing the transaction. Please refresh and try again.',
            'onTransactionDeleted -- error marking transaction reversed for transaction id : ' + shInvoiceTransaction.id,
          );
        });
      },
    });
  }

  /**
   *
   */
  @action
  downloadPDF() {
    window.open(absoluteStoryhunterUrl(`/sh-invoices/${this.state.shInvoice.id}/pdf`));
  }

  /**
   * @param {ShInvoiceTransaction} shInvoiceTransaction
   */
  @action
  editTransaction(shInvoiceTransaction) {
    let modalModel = {
      shInvoice: this.state.shInvoice,
      shInvoiceTransaction: shInvoiceTransaction,
      onTransactionEdited: () => {
        this.applicationStateManager.addToast('Transaction Edited');
        this.stateManager.loadShInvoice(this.state.shInvoice.id);
      }
    };

    showModal(this, 'admin/edit-sh-invoice-transaction-modal', modalModel);
  }

  /**
   *
   */
  @action
  onAddToBatchClicked() {
    let modalModel = {
      title: `Add Invoice Ids to Batch`,
      textAreaPlaceholder: '123,456,789,...',
      cancelText: 'Cancel',
      confirmText: 'Add Invoices',
      onConfirmMessage: (invoiceIdsText) => {
        this.stateManager.addInvoicesToBatch(invoiceIdsText);
      }
    };

    showModal(this, 'lsg/lsg-modal-text-editor', modalModel);
  }

  /**
   *
   */
  @action
  onCancelEditMemoClicked() {
    this.stateManager.toggleIsEditingMemo(false);
  }

  /**
   *
   */
  @action
  onEditMemoClicked() {
    this.stateManager.toggleIsEditingMemo(true);
  }

  /**
   *
   */
  @action
  onUpdateMemoText(text) {
    this.stateManager.updateInvoiceMemoInput(text);
  }

  /**
   * What mark all paid on batched invoice button is clicked.
   */
  @action
  markPaid() {
    let modalModel = {
      shInvoice: this.state.shInvoice,
      shInvoiceTransaction: null,
      onTransactionEdited: () => {
        this.applicationStateManager.addToast('Invoice Marked Paid');
        this.stateManager.loadShInvoice(this.state.shInvoice.id);
      },
      markFullyPaid: true,
    };

    showModal(this, 'admin/edit-sh-invoice-transaction-modal', modalModel);
  }

  /**
   * @param {KeyboardEvent} e
   */
  @action
  markRevised(e) {
    const revision = this.state.shInvoice.revision;
    showModal(this, 'confirmation', {
      isShowCancelButton: true,
      titleText: 'Are you sure?',
      bodyText: `Revision will be updated from ${revision} to ${revision + 1}`,
      confirmAction: () => {
        this.shInvoiceCommandService.updateRevision(this.state.shInvoice, revision + 1).then(() => {
          this.applicationStateManager.addToast(`Revision changed to ${revision + 1}`);
          this.stateManager.loadShInvoice(this.state.shInvoice.id);
        });
      }
    });
  }

  @action
  onReconcileBilling() {
    showModal(this, 'confirmation', {
      titleText: 'Reconcile Billing',
      bodyText: "This will re-calculate the application's billing, and set payout, process payment, send emails, etc. if appropriate.",
      actionButtonText: 'Okay',
      isShowCancelButton: true,
      confirmAction: () => {
        this.applicationCommandService.reconcileBilling(this.state.shInvoice.application)
          .then(() => {
            this.applicationStateManager.addToast('Billing reconciled');
          })
          .catch((e) => {
            handleErrorResponseWithModal(
              this,
              e,
              'There was an error reconciling billing. Please contact engineering for support.',
              `reconcileBilling -- Failing reconciling app id: ${this.state.shPayment.application.id}`,
            );
          });
      },
    });
  }

  /**
   *
   */
  @action
  onSaveMemoClicked() {
    this.stateManager.updateInvoiceMemo();
  }

  /**
   *
   */
  @action
  onUnBatchInvoiceClicked() {
    let modalData = {
      titleText: "Un-batch Invoice?",
      bodyText: "Are you sure you want to un-batch this invoice?",
      actionButtonText: 'Yes',
      cancelButtonText: 'No',
      isShowCancelButton: true,
      confirmAction: () => {
        this.stateManager.unBatchInvoice().then(() => {
          this.applicationStateManager.setNextRoute(
            EMBER_ROUTE_NAMES.ADMIN__ACCOUNTING__PAYIN);
        });
      },
    };

    showModal(this, 'confirmation', modalData);
  }

  /**
   *
   */
  @action
  onEditFeesClicked() {
    const modalModel = {
      relatedModel: this.state.shInvoice,
      showBuyerFees: true,
      showSellerFees: false,
      onFeesUpdated: (madeFMS) => {
        this.stateManager.feesUpdated(madeFMS);
      }
    };

    showModal(this, 'projects/edit-project-fees-modal', modalModel);
  }

  @action
  processShInvoice() {
    this.stateManager.processShInvoice.perform();
  }

  @action
  removeFromBatch(childShInvoiceId) {
    showModal(this, 'confirmation', {
      isShowCancelButton: true,
      titleText: 'WARNING!',
      bodyText: 'This batched invoice will be recalculated, but will NOT be automatically re-sent. Once you have completed your changes you should manually send the batched invoice again.',
      confirmAction: () => {
        this._removeFromBatchTask.perform(childShInvoiceId);
      }
    });
  }

  /**
   */
  @action
  sendBillableRecord() {
    this.stateManager.sendInvoice();
  }
}

export default AdminAccountingPayinInvoiceController;
